import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { generateSignedUrl } from '@/api/user';
import { LoadingOutlined } from '@ant-design/icons';

interface VoiceAudioPlayerProps {
	fileName: string;
	db: string;
	onPlay?: () => void;
	onPause?: () => void;
}

const VoiceAudioPlayer: React.FC<VoiceAudioPlayerProps> = ({ fileName, db, onPlay, onPause }) => {
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	const { mutate, isPending } = useMutation({
		mutationFn: generateSignedUrl,
		onSuccess: ({ url }) => {
			if (audioRef.current && url) {
				audioRef.current.src = url;
				audioRef.current.play().then(() => {
					setIsPlaying(true);
					onPlay?.();
				});
			}
		},
		onError: (error) => {
			console.error('Failed to fetch presigned URL:', error);
		},
	});

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const handleEnded = () => {
			setIsPlaying(false);
			onPause?.();
		};

		audio.addEventListener('ended', handleEnded);
		return () => {
			audio.removeEventListener('ended', handleEnded);
		};
	}, [onPause]);

	const togglePlayPause = (e: React.MouseEvent) => {
		// Prevent event from bubbling up to parent elements
		e.stopPropagation();
		e.preventDefault();

		if (audioRef.current) {
			if (audioRef.current.paused) {
				if (!audioRef.current.src) {
					// If no src is set, fetch the presigned URL
					mutate({
						objectKey: `${db}/${fileName}`,
						bucketType: 'voices',
					});
				} else {
					audioRef.current.play().then(() => {
						setIsPlaying(true);
						onPlay?.();
					});
				}
			} else {
				audioRef.current.pause();
				setIsPlaying(false);
				onPause?.();
			}
		}
	};

	return (
		<div onClick={(e) => e.stopPropagation()}>
			<audio ref={audioRef} preload="none" />
			<button
				type="button"
				onClick={togglePlayPause}
				disabled={isPending}
				className={`flex items-center justify-center w-8 h-8 rounded-full text-white focus:outline-none transition-colors ${
					isPlaying ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400 hover:bg-gray-500'
				} disabled:bg-gray-300 disabled:cursor-not-allowed`}
			>
				{isPending ? (
					<LoadingOutlined size={16} />
				) : isPlaying ? (
					<Pause size={16} />
				) : (
					<Play size={16} />
				)}
			</button>
		</div>
	);
};

export default VoiceAudioPlayer;
